import { useCallback, useEffect, useState } from 'react'
import { api } from '../../../lib/api'
import type { SyncSchedule, SyncScheduleCreate, SyncScheduleUpdate } from '../types'

type UseSyncSchedulesReturn = {
  schedules: SyncSchedule[]
  loading: boolean
  error: string | null
  
  // Actions
  reload: () => Promise<void>
  create: (data: SyncScheduleCreate) => Promise<SyncSchedule | null>
  update: (publicId: string, data: SyncScheduleUpdate) => Promise<boolean>
  remove: (publicId: string) => Promise<boolean>
  toggle: (publicId: string) => Promise<boolean>
  trigger: (publicId: string) => Promise<{ task_id: string } | null>
  
  // Helpers
  getByPublicId: (publicId: string) => SyncSchedule | undefined
}

/**
 * Hook pour gérer les configurations de synchronisation WordPress
 * Responsabilité unique : CRUD schedules
 */
export function useSyncSchedules(): UseSyncSchedulesReturn {
  const [schedules, setSchedules] = useState<SyncSchedule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get('/wp/schedules')
      setSchedules(data.items || [])
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur chargement configurations')
    } finally {
      setLoading(false)
    }
  }, [])

  const create = useCallback(async (formData: SyncScheduleCreate): Promise<SyncSchedule | null> => {
    try {
      const { data } = await api.post('/wp/schedules', formData)
      await reload()
      return data
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur création configuration')
      return null
    }
  }, [reload])

  const update = useCallback(async (publicId: string, formData: SyncScheduleUpdate): Promise<boolean> => {
    try {
      await api.put(`/wp/schedules/${publicId}`, formData)
      await reload()
      return true
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur mise à jour configuration')
      return false
    }
  }, [reload])

  const remove = useCallback(async (publicId: string): Promise<boolean> => {
    try {
      await api.delete(`/wp/schedules/${publicId}`)
      await reload()
      return true
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur suppression configuration')
      return false
    }
  }, [reload])

  const toggle = useCallback(async (publicId: string): Promise<boolean> => {
    try {
      await api.patch(`/wp/schedules/${publicId}/toggle`)
      await reload()
      return true
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur activation/désactivation')
      return false
    }
  }, [reload])

  const trigger = useCallback(async (publicId: string): Promise<{ task_id: string } | null> => {
    try {
      const { data } = await api.post(`/wp/schedules/${publicId}/trigger`)
      return data
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      if (e?.response?.status === 429) {
        setError(detail || 'Veuillez attendre avant de relancer')
      } else if (e?.response?.status === 409) {
        setError(detail || 'Une synchronisation est déjà en cours')
      } else {
        setError(detail || 'Erreur déclenchement synchronisation')
      }
      return null
    }
  }, [])

  const getByPublicId = useCallback((publicId: string) => {
    return schedules.find(s => s.public_id === publicId)
  }, [schedules])

  // Chargement initial
  useEffect(() => {
    reload()
  }, [reload])

  return {
    schedules,
    loading,
    error,
    reload,
    create,
    update,
    remove,
    toggle,
    trigger,
    getByPublicId,
  }
}

export default useSyncSchedules
